import { useCallback, useEffect, useMemo, useState } from 'react';
import ApiClient from '../config/ApiClient';
import {
  IntakeAnswers,
  IntakeCondition,
  IntakeLane,
  IntakeQuestion,
  IntakeSchema,
  IntakeSectionGroup,
  IntakeSubmitResponse,
} from '../types/intakeSurvey';

const HONEYPOT_FIELD = 'website';

function getFieldValue(answers: IntakeAnswers, field: string): string | number | string[] | undefined {
  return answers[field];
}

function matchesCondition(answers: IntakeAnswers, condition?: IntakeCondition | { all: IntakeCondition[] }): boolean {
  if (!condition) return true;
  if ('all' in condition) {
    return condition.all.every((c) => matchesCondition(answers, c));
  }
  const value = getFieldValue(answers, condition.field);
  if (condition.empty !== undefined) {
    const isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
    return condition.empty ? isEmpty : !isEmpty;
  }
  if (condition.eq !== undefined) {
    return String(value) === condition.eq;
  }
  const num = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  if (condition.lt !== undefined) {
    if (Number.isNaN(num)) return false;
    return num < condition.lt;
  }
  if (condition.gte !== undefined) {
    if (Number.isNaN(num)) return false;
    return num >= condition.gte;
  }
  return true;
}

export function isQuestionVisible(question: IntakeQuestion, answers: IntakeAnswers): boolean {
  if (question.hide_when && matchesCondition(answers, question.hide_when)) {
    return false;
  }
  if (question.show_when && !matchesCondition(answers, question.show_when)) {
    return false;
  }
  return true;
}

export function isQuestionRequired(question: IntakeQuestion, answers: IntakeAnswers): boolean {
  if (question.required) return true;
  if (question.required_when) {
    return matchesCondition(answers, question.required_when);
  }
  return false;
}

export function resolveQuestionLabel(question: IntakeQuestion, answers: IntakeAnswers): string {
  for (const rule of question.label_when ?? []) {
    if (matchesCondition(answers, rule.when)) {
      return rule.label;
    }
  }
  return question.label;
}

export function visibleQuestions(schema: IntakeSchema | null, answers: IntakeAnswers): IntakeQuestion[] {
  if (!schema) return [];
  return schema.questions.filter((q) => isQuestionVisible(q, answers));
}

export function visibleSectionGroups(schema: IntakeSchema | null, answers: IntakeAnswers): IntakeSectionGroup[] {
  if (!schema?.sections?.length) return [];

  const visible = visibleQuestions(schema, answers);
  const sectionsById = new Map(schema.sections.map((section) => [section.id, section]));
  const grouped = new Map<string, IntakeQuestion[]>();

  for (const question of visible) {
    const sectionId = question.section;
    const bucket = grouped.get(sectionId) ?? [];
    bucket.push(question);
    grouped.set(sectionId, bucket);
  }

  return schema.sections
    .filter((section) => grouped.has(section.id))
    .map((section) => ({
      section,
      questions: grouped.get(section.id) ?? [],
    }));
}

export function useIntakeSurvey(lane: IntakeLane) {
  const [schema, setSchema] = useState<IntakeSchema | null>(null);
  const [answers, setAnswers] = useState<IntakeAnswers>({});
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [completed, setCompleted] = useState(false);
  const [completionMessage, setCompletionMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await ApiClient.get<IntakeSchema>(`/api/intake/${lane}/schema/`);
      if (cancelled) return;
      if (res.success && res.data) {
        setSchema(res.data);
      } else {
        setError(res.error ?? 'Unable to load survey.');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [lane]);

  const visibleFields = useMemo(() => visibleQuestions(schema, answers), [schema, answers]);
  const visibleSections = useMemo(() => visibleSectionGroups(schema, answers), [schema, answers]);

  const setAnswer = useCallback((id: string, value: string | number | string[]) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setFieldErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const validateAll = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    for (const question of visibleFields) {
      const value = answers[question.id];
      const required = isQuestionRequired(question, answers);
      const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
      if (required && empty) {
        errors[question.id] = 'This field is required.';
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }
    return true;
  }, [answers, visibleFields]);

  const submit = useCallback(async () => {
    if (!validateAll()) {
      setError('Please update the highlighted fields.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    const visibleIds = new Set(visibleFields.map((q) => q.id));
    const payloadAnswers: IntakeAnswers = {};
    for (const [key, val] of Object.entries(answers)) {
      if (visibleIds.has(key)) {
        payloadAnswers[key] = val;
      }
    }

    const res = await ApiClient.post<IntakeSubmitResponse>(`/api/intake/${lane}/submit/`, {
      answers: payloadAnswers,
      [HONEYPOT_FIELD]: honeypot,
      source_url: window.location.href,
    });

    setSubmitting(false);
    if (res.success && res.data?.success) {
      setCompleted(true);
      setCompletionMessage(res.data.message ?? 'Thank you for your submission.');
      return;
    }

    if (res.errors) {
      const mapped: Record<string, string> = {};
      for (const [key, val] of Object.entries(res.errors)) {
        mapped[key] = Array.isArray(val) ? val.join(' ') : String(val);
      }
      setFieldErrors(mapped);
      setError('Please update the highlighted fields.');
      return;
    }
    setError(res.error ?? res.data?.detail ?? 'Submission failed. Please try again.');
  }, [answers, honeypot, lane, validateAll, visibleFields]);

  return {
    schema,
    answers,
    honeypot,
    setHoneypot,
    visibleFields,
    visibleSections,
    loading,
    submitting,
    error,
    fieldErrors,
    completed,
    completionMessage,
    setAnswer,
    submit,
  };
}

export { HONEYPOT_FIELD };
